import React, { useState, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Chip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import styles from "./intake-agent-form.module.scss";

type IntakeAgentFormProps = {
  parsedContent: any;
};

// Language options
const LANGUAGE_OPTIONS = [
  { value: "english", label: "English" },
] as const;


// File validation constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_FILE_TYPES = [".pdf", ".doc", ".docx"] as const;

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const validateFileType = (file: File): boolean => {
  const extension = "." + file.name.split(".").pop()?.toLowerCase();
  return ACCEPTED_FILE_TYPES.includes(
    extension as (typeof ACCEPTED_FILE_TYPES)[number],
  );
};

const validateFileSize = (file: File): boolean => {
  return file.size <= MAX_FILE_SIZE;
};

export default function IntakeAgentForm({
  parsedContent,
}: IntakeAgentFormProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  // Memoize session ID
  const sessionId = useMemo(
    () => searchParams.get("sessionId"),
    [searchParams],
  );

  // Get current agent info
  const currentAgent = useSelector(
    (state: RootState) => state.agents?.currentAgent,
  );
  const agentLoading = useSelector((state: RootState) => state.user.loading);

  // Form state
  const [candidateId, setCandidateId] = useState("");
  const [role, setRole] = useState("");
  const [jobFamily, setJobFamily] = useState("");
  const [language, setLanguage] = useState("english");
  const [roleContext, setRoleContext] = useState("");

  // File upload state
  const [jdFile, setJdFile] = useState<File | null>(null);
  const [jdText, setJdText] = useState("");
  const [jdInputMode, setJdInputMode] = useState<"file" | "text">("file");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [prescreeningFile, setPrescreeningFile] = useState<File | null>(null);
  const [prescreeningText, setPrescreeningText] = useState("");
  const [prescreeningInputMode, setPrescreeningInputMode] = useState<"file" | "text">("file");

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // File input refs
  const jdInputRef = useRef<HTMLInputElement>(null);
  const resumeInputRef = useRef<HTMLInputElement>(null);
  const prescreeningInputRef = useRef<HTMLInputElement>(null);

  // File handlers
  const handleFileSelect = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      setter: React.Dispatch<React.SetStateAction<File | null>>,
    ) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!validateFileType(file)) {
        setError("Only PDF, DOC, and DOCX files are allowed");
        e.target.value = "";
        return;
      }

      // Validate file size
      if (!validateFileSize(file)) {
        setError("File size must be less than 5MB");
        e.target.value = "";
        return;
      }

      setter(file);
      setError(null);
      // Reset input value
      e.target.value = "";
    },
    [],
  );

  const handleRemoveFile = useCallback(
    (setter: React.Dispatch<React.SetStateAction<File | null>>) => {
      setter(null);
    },
    [],
  );

  // Clear specific field error
  const clearFieldError = useCallback((fieldName: string) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  // Validation
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!candidateId.trim()) {
      errors.candidateId = "Candidate ID is required";
    }

    if (!role.trim()) {
      errors.role = "Role is required";
    }

    if (!jobFamily) {
      errors.jobFamily = "Job family is required";
    }

    if (!language) {
      errors.language = "Language is required";
    }

    if (!roleContext.trim()) {
      errors.roleContext = "Role context is required";
    }

    // Job Description is mandatory - either file or text
    if (jdInputMode === "file" && !jdFile) {
      errors.jd = "Job Description file is required";
    } else if (jdInputMode === "text" && !jdText.trim()) {
      errors.jd = "Job Description text is required";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [candidateId, role, jobFamily, language, roleContext, jdInputMode, jdFile, jdText]);

  // Form submission
  const handleSubmit = useCallback(async () => {
    setError(null);

    if (!validateForm()) {
      setError("Please fill in all required fields");
      return;
    }

    if (!currentAgent) {
      setError("Agent not initialized");
      return;
    }

    const questionId = uuid();
    const timestamp = new Date().toISOString();

    // Build the message payload as a stringified JSON
    const messageData = {
      candidate_id: candidateId.trim(),
      role: role.trim(),
      job_family: jobFamily,
      language: language,
      role_context: roleContext.trim() || undefined,
    };

    const payload: any = {
      agentId: currentAgent.id,
      message: JSON.stringify(messageData),
      questionId,
      timestamp,
      agentEndpoint: currentAgent.endpoint,
      sessionId,
    };

    // Add specific file parameters for intake agent
    // For JD, send either file or text as a blob in the same parameter
    if (jdInputMode === "file" && jdFile) {
      payload.interview_jd = jdFile;
    } else if (jdInputMode === "text" && jdText.trim()) {
      // Convert text to a Blob/File to send in the same parameter
      const jdBlob = new Blob([jdText], { type: "text/plain" });
      const jdTextFile = new File([jdBlob], "job-description.txt", {
        type: "text/plain",
      });
      payload.interview_jd = jdTextFile;
    }

    if (resumeFile) {
      payload.interview_resume = resumeFile;
    }

    // For Pre-screening, send either file or text as a blob in the same parameter
    if (prescreeningInputMode === "file" && prescreeningFile) {
      payload.interview_prescreening = prescreeningFile;
    } else if (prescreeningInputMode === "text" && prescreeningText.trim()) {
      // Convert text to a Blob/File to send in the same parameter
      const prescreeningBlob = new Blob([prescreeningText], { type: "text/plain" });
      const prescreeningTextFile = new File([prescreeningBlob], "pre-screening.txt", {
        type: "text/plain",
      });
      payload.interview_prescreening = prescreeningTextFile;
    }

    try {
      await dispatch(sendMessageToAgent(payload)).unwrap();
      // Form submitted successfully - the chat will update with agent response
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to submit form. Please try again.";
      setError(errorMessage);
    }
  }, [
    currentAgent,
    candidateId,
    role,
    jobFamily,
    language,
    roleContext,
    jdFile,
    jdText,
    jdInputMode,
    resumeFile,
    prescreeningFile,
    prescreeningText,
    prescreeningInputMode,
    sessionId,
    validateForm,
    dispatch,
  ]);

  return (
    <Box className={styles.container}>
      <Paper className={styles.formPaper} elevation={0}>
        {/* Header */}
        <Box className={styles.header}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#004141", mb: 1 }}
          >
            Set up the interview to get started
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", fontSize: "13px" }}>
            Provide the candidate and role details and the job description.
            We'll validate the stage, factor in pre-screening, and generate a
            structured, competency-based question set.
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Divider sx={{ mb: 3 }} />

        {/* Form Fields */}
        <Box className={styles.formGrid}>
          {/* Candidate ID */}
          <TextField
            fullWidth
            label="Candidate ID"
            placeholder="e.g., CAN-2026-00123"
            value={candidateId}
            onChange={(e) => {
              setCandidateId(e.target.value);
              clearFieldError("candidateId");
            }}
            error={!!fieldErrors.candidateId}
            helperText={fieldErrors.candidateId}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                backgroundColor: "#fafafa",
                fontSize: "13px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "12px 14px",
                fontSize: "13px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                transform: "translate(14px, 12px) scale(1)",
                "&.MuiInputLabel-shrink": {
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              },
            }}
          />

          {/* Role */}
          <TextField
            fullWidth
            label="Role"
            placeholder="e.g., Data Engineer"
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              clearFieldError("role");
            }}
            error={!!fieldErrors.role}
            helperText={fieldErrors.role}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                backgroundColor: "#fafafa",
                fontSize: "13px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "12px 14px",
                fontSize: "13px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                transform: "translate(14px, 12px) scale(1)",
                "&.MuiInputLabel-shrink": {
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              },
            }}
          />

          {/* Job Family */}
          <TextField
            fullWidth
            label="Job Family"
            placeholder="e.g., Engineering"
            value={jobFamily}
            onChange={(e) => {
              setJobFamily(e.target.value);
              clearFieldError("jobFamily");
            }}
            error={!!fieldErrors.jobFamily}
            helperText={fieldErrors.jobFamily}
            required
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                backgroundColor: "#fafafa",
                fontSize: "13px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "12px 14px",
                fontSize: "13px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                transform: "translate(14px, 12px) scale(1)",
                "&.MuiInputLabel-shrink": {
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              },
            }}
          />

          {/* Language */}
          <FormControl
            fullWidth
            required
            error={!!fieldErrors.language}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                backgroundColor: "#fafafa",
                fontSize: "13px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                transform: "translate(14px, 12px) scale(1)",
                "&.MuiInputLabel-shrink": {
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              },
              "& .MuiSelect-select": {
                padding: "12px 14px",
                fontSize: "13px",
              },
            }}
          >
            <InputLabel>Language</InputLabel>
            <Select
              value={language}
              label="Language"
              onChange={(e) => {
                setLanguage(e.target.value);
                clearFieldError("language");
              }}
            >
              {LANGUAGE_OPTIONS.map((lang) => (
                <MenuItem key={lang.value} value={lang.value}>
                  {lang.label}
                </MenuItem>
              ))}
            </Select>
            {fieldErrors.language && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, ml: 2 }}
              >
                {fieldErrors.language}
              </Typography>
            )}
          </FormControl>

          {/* Role Context - Full width */}
          <TextField
            fullWidth
            label="Role Context"
            placeholder="e.g., Owns batch and streaming pipelines for the AI platform"
            value={roleContext}
            onChange={(e) => {
              setRoleContext(e.target.value);
              clearFieldError("roleContext");
            }}
            error={!!fieldErrors.roleContext}
            helperText={fieldErrors.roleContext || "Provide additional context about the role"}
            multiline
            rows={3}
            required
            sx={{
              gridColumn: "1 / -1",
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
                backgroundColor: "#fafafa",
                fontSize: "13px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "12px 14px",
                fontSize: "13px",
              },
              "& .MuiInputLabel-root": {
                fontSize: "13px",
                transform: "translate(14px, 12px) scale(1)",
                "&.MuiInputLabel-shrink": {
                  transform: "translate(14px, -9px) scale(0.75)",
                },
              },
              "& .MuiFormHelperText-root": {
                fontSize: "12px",
              },
            }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* File Uploads Section */}
        <Box className={styles.fileSection}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#004141", mb: 1, fontSize: "15px" }}
          >
            Upload Documents
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#666", mb: 2, fontSize: "13px" }}
          >
            Upload relevant documents. Supported formats: PDF, DOC, DOCX (Max 5MB each)
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Job Description - Required */}
            <Box
              sx={{
                gridColumn: "1 / -1",
                border: `1px solid ${fieldErrors.jd ? "#d32f2f" : "#e0e0e0"}`,
                borderRadius: "8px",
                p: 2,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#004141", fontSize: "14px" }}
                  >
                    Job Description
                  </Typography>
                  <Chip
                    label="Required"
                    size="small"
                    sx={{
                      backgroundColor: "#fadada",
                      color: "#bd1919",
                      fontSize: "11px",
                      height: 20,
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                    padding: "4px",
                    gap: "4px",
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => {
                      setJdInputMode("file");
                      setJdText("");
                      clearFieldError("jd");
                    }}
                    sx={{
                      fontSize: "12px",
                      textTransform: "none",
                      color: jdInputMode === "file" ? "#004141" : "#666",
                      backgroundColor: jdInputMode === "file" ? "#fff" : "transparent",
                      border: "none",
                      borderRadius: "4px",
                      padding: "6px 12px",
                      minWidth: "auto",
                      boxShadow: jdInputMode === "file" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                      "&:hover": {
                        backgroundColor: jdInputMode === "file" ? "#fff" : "#e8f5f3",
                      },
                    }}
                  >
                    Upload File
                  </Button>
                  <Button
                    size="small"
                    startIcon={<TextFieldsIcon />}
                    onClick={() => {
                      setJdInputMode("text");
                      setJdFile(null);
                      clearFieldError("jd");
                    }}
                    sx={{
                      fontSize: "12px",
                      textTransform: "none",
                      color: jdInputMode === "text" ? "#004141" : "#666",
                      backgroundColor: jdInputMode === "text" ? "#fff" : "transparent",
                      border: "none",
                      borderRadius: "4px",
                      padding: "6px 12px",
                      minWidth: "auto",
                      boxShadow: jdInputMode === "text" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                      "&:hover": {
                        backgroundColor: jdInputMode === "text" ? "#fff" : "#e8f5f3",
                      },
                    }}
                  >
                    Paste Text
                  </Button>
                </Box>
              </Box>

              {jdInputMode === "file" ? (
                <>
                  <input
                    ref={jdInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      handleFileSelect(e, setJdFile);
                      clearFieldError("jd");
                    }}
                    style={{ display: "none" }}
                  />
                  {jdFile ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 32, color: "#00a888" }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#004141", fontSize: "13px" }}
                        >
                          {jdFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#666", fontSize: "12px" }}>
                          {formatFileSize(jdFile.size)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile(setJdFile)}
                        sx={{ color: "#dc3545" }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                        border: "2px dashed #e0e0e0",
                        cursor: "pointer",
                      }}
                      onClick={() => jdInputRef.current?.click()}
                    >
                      <CloudUploadIcon sx={{ fontSize: 40, color: "#00a888", mb: 1 }} />
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", fontSize: "13px", mb: 1 }}
                      >
                        Click to upload or drag and drop
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#888", fontSize: "12px" }}>
                        PDF, DOC, DOCX (Max 5MB)
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  placeholder="Paste the job description text here..."
                  value={jdText}
                  onChange={(e) => {
                    setJdText(e.target.value);
                    clearFieldError("jd");
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      backgroundColor: "#fafafa",
                      fontSize: "13px",
                    },
                    "& .MuiOutlinedInput-input": {
                      fontSize: "13px",
                    },
                  }}
                />
              )}

              {fieldErrors.jd && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "block", fontSize: "12px" }}
                >
                  {fieldErrors.jd}
                </Typography>
              )}
            </Box>

            {/* Resume Upload */}
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                p: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#004141", fontSize: "14px" }}
                >
                  Candidate Resume
                </Typography>
                <Chip
                  label="Optional"
                  size="small"
                  sx={{
                    backgroundColor: "#e8f5f3",
                    color: "#004141",
                    fontSize: "11px",
                    height: 20,
                  }}
                />
              </Box>

              <input
                ref={resumeInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => handleFileSelect(e, setResumeFile)}
                style={{ display: "none" }}
              />

              {resumeFile ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                  }}
                >
                  <DescriptionIcon sx={{ fontSize: 32, color: "#00a888" }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#004141", fontSize: "13px" }}
                    >
                      {resumeFile.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#666", fontSize: "12px" }}>
                      {formatFileSize(resumeFile.size)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(setResumeFile)}
                    sx={{ color: "#dc3545" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                    border: "2px dashed #e0e0e0",
                    cursor: "pointer",
                  }}
                  onClick={() => resumeInputRef.current?.click()}
                >
                  <CloudUploadIcon sx={{ fontSize: 40, color: "#00a888", mb: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{ color: "#666", fontSize: "13px", mb: 1 }}
                  >
                    Click to upload or drag and drop
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#888", fontSize: "12px" }}>
                    PDF, DOC, DOCX (Max 5MB)
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Pre-screening Upload */}
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                p: 2,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "#004141", fontSize: "14px" }}
                  >
                    Pre-screening
                  </Typography>
                  <Chip
                    label="Optional"
                    size="small"
                    sx={{
                      backgroundColor: "#e8f5f3",
                      color: "#004141",
                      fontSize: "11px",
                      height: 20,
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "inline-flex",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                    padding: "4px",
                    gap: "4px",
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => {
                      setPrescreeningInputMode("file");
                      setPrescreeningText("");
                    }}
                    sx={{
                      fontSize: "12px",
                      textTransform: "none",
                      color: prescreeningInputMode === "file" ? "#004141" : "#666",
                      backgroundColor: prescreeningInputMode === "file" ? "#fff" : "transparent",
                      border: "none",
                      borderRadius: "4px",
                      padding: "6px 12px",
                      minWidth: "auto",
                      boxShadow: prescreeningInputMode === "file" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                      "&:hover": {
                        backgroundColor: prescreeningInputMode === "file" ? "#fff" : "#e8f5f3",
                      },
                    }}
                  >
                    Upload File
                  </Button>
                  <Button
                    size="small"
                    startIcon={<TextFieldsIcon />}
                    onClick={() => {
                      setPrescreeningInputMode("text");
                      setPrescreeningFile(null);
                    }}
                    sx={{
                      fontSize: "12px",
                      textTransform: "none",
                      color: prescreeningInputMode === "text" ? "#004141" : "#666",
                      backgroundColor: prescreeningInputMode === "text" ? "#fff" : "transparent",
                      border: "none",
                      borderRadius: "4px",
                      padding: "6px 12px",
                      minWidth: "auto",
                      boxShadow: prescreeningInputMode === "text" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                      "&:hover": {
                        backgroundColor: prescreeningInputMode === "text" ? "#fff" : "#e8f5f3",
                      },
                    }}
                  >
                    Paste Text
                  </Button>
                </Box>
              </Box>

              {prescreeningInputMode === "file" ? (
                <>
                  <input
                    ref={prescreeningInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => handleFileSelect(e, setPrescreeningFile)}
                    style={{ display: "none" }}
                  />
                  {prescreeningFile ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 2,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 32, color: "#00a888" }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#004141", fontSize: "13px" }}
                        >
                          {prescreeningFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#666", fontSize: "12px" }}>
                          {formatFileSize(prescreeningFile.size)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile(setPrescreeningFile)}
                        sx={{ color: "#dc3545" }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                        border: "2px dashed #e0e0e0",
                        cursor: "pointer",
                      }}
                      onClick={() => prescreeningInputRef.current?.click()}
                    >
                      <CloudUploadIcon sx={{ fontSize: 40, color: "#00a888", mb: 1 }} />
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", fontSize: "13px", mb: 1 }}
                      >
                        Click to upload or drag and drop
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#888", fontSize: "12px" }}>
                        PDF, DOC, DOCX (Max 5MB)
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  placeholder="Paste the pre-screening text here..."
                  value={prescreeningText}
                  onChange={(e) => setPrescreeningText(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      backgroundColor: "#fafafa",
                      fontSize: "13px",
                    },
                    "& .MuiOutlinedInput-input": {
                      fontSize: "13px",
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Submit Button */}
        <Box className={styles.submitSection}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={agentLoading}
            endIcon={
              agentLoading ? (
                <CircularProgress size={20} sx={{ color: "#004141" }} />
              ) : (
                <SendIcon />
              )
            }
            sx={{
              backgroundColor: "#00d2aa",
              color: "#004141",
              fontWeight: 600,
              padding: "10px 24px",
              fontSize: "13px",
              textTransform: "none",
              borderRadius: "6px",
              "&:hover": {
                backgroundColor: "#00b896",
              },
              "&:disabled": {
                backgroundColor: "#e0e0e0",
                color: "#999",
              },
            }}
          >
            {agentLoading ? "Processing..." : "Generate Interview Pack"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
